import * as Api from '@ViewModels';
import { ComposeEmailViewModel } from '../../viewmodels/AppViewModels';
import { CampaignType, ITemplatesIndustryWithCategories } from '../AdminModels';
import { IContentCalendarSelectedSuggestion } from '../index';
import { IPixabayImage, IPixabayImageSearchOptions } from '../Pixabay';

/**
 * Gets categories by name
 * @source Original location: UiUtils.ts.backup line 2576
 */
export const getCategoriesByName = (catOptions: ITemplatesIndustryWithCategories[], selectedOption: string) => {
	const list = catOptions.filter(x => x.categoryNames.includes(selectedOption));
	const newList: Api.ITemplateStatus[] = list.map(x => {
		return {
			industry: x.industry,
			category: selectedOption,
		};
	});
	return newList;
};

/**
 * Gets category path by campaign type
 * @source Original location: UiUtils.ts.backup line 2587
 */
export const getCategoryPathByCampaignType = (campaign: CampaignType, isCombo?: boolean) => {
	let categoriesType = '';
	switch (campaign) {
		case CampaignType.Social:
			categoriesType = 'socialCategories';
			break;
		case CampaignType.Blog:
			categoriesType = 'blogCategories';
			break;
		case CampaignType.Email:
			categoriesType = 'categories';
			break;
		default:
			categoriesType = 'categories';
			break;
	}
	if (campaign === CampaignType.Social && isCombo) {
		categoriesType = 'socialCategories';
	}
	return categoriesType;
};

/**
 * Handles name update for selected image
 * @source Original location: UiUtils.ts.backup line 2609
 */
export const handleNameUpdateForSelectedImage = (
	image: IPixabayImage,
	options?: IPixabayImageSearchOptions
): Api.IFileAttachmentWithURL => {
	const name = image.previewURL.split('/').pop();
	const fileName = name.split('_').shift();
	return {
		...image,
		fileName,
		url:
			Math.max(options?.min_height || 0, options?.min_width || 0) > 640 && Boolean(image.largeImageURL)
				? image.largeImageURL
				: image.webformatURL,
		fileSize: image.imageSize,
		source: Api.IAttachmentUploadSource.Pixabay,
	};
};

/**
 * Applies default excluded tags to contacts filter request
 * @source Original location: UiUtils.ts.backup line 2727
 */
export const applyDefaultExcludedTagsToContactsFilterRequest = (
	emailComposer: ComposeEmailViewModel,
	contactFilterRequest?: Api.IContactsFilterRequest
): Api.IContactsFilterRequest => {
	const sorted = Api.VmUtils.sortContactFilterCriteria(contactFilterRequest);
	const excludes = Array.from(Api.DefaultBulkSendExcludedTags);

	let newCriteria = Api.VmUtils.concatContactFilterCriteria(
		[
			...sorted.compound,
			...sorted.filters,
			...sorted.searches.filter(
				x =>
					x.op !== Api.FilterOperator.Not ||
					(x.op === Api.FilterOperator.Not &&
						!excludes.find(y => y.toLocaleLowerCase() === x.value?.toLocaleLowerCase()))
			),
		],
		Array.from(Api.DefaultBulkSendExcludedTags).map(x => ({
			op: Api.FilterOperator.Not,
			property: Api.ContactFilterCriteriaProperty.Tag,
			value: x,
		}))
	);

	if (
		emailComposer?.emailMessage?.isSendFromOption(Api.SendEmailFrom.ContactOwner) ||
		emailComposer?.emailMessage?.isSendFromOption(Api.SendEmailFrom.SelectedUser)
	) {
		newCriteria = [
			...newCriteria,
			{
				op: Api.FilterOperator.Not,
				property: Api.ContactFilterCriteriaProperty.PrivateContacts,
				value: 'Private',
			},
		];
	}

	return { criteria: newCriteria };
};

export const getUniqueIdForSuggestion = (suggestion: IContentCalendarSelectedSuggestion) => {
	if (suggestion.dragDropId) {
		return suggestion.dragDropId;
	}
	if (suggestion.id) {
		return `${suggestion.id}-${suggestion.templateReference.templateId}`;
	}
	return suggestion.templateReference.templateId;
};

export function getDefaultTemplateOrSignature(
	templates: Api.ITemplate[],
	signatures: Api.ITemplate[],
	defaultTemplateId?: string
): Api.ITemplate | undefined {
	const defaultTemplate = templates?.find(t => t.id === defaultTemplateId);
	return defaultTemplate || signatures?.[0] || undefined;
}

/**
 * Sorts email signatures by default
 * @source Original location: UiUtils.ts.backup line 2900
 */
export function sortEmailSignaturesByDefault(templates: Api.ITemplate[]) {
	return (
		templates?.sort((a, b) => {
			if (!!a.defaultTemplate && !b.defaultTemplate) {
				return -1;
			} else if (!!b.defaultTemplate && !a.defaultTemplate) {
				return 1;
			} else {
				return a.lastModifiedDate < b.lastModifiedDate ? -1 : 1;
			}
		}) || []
	);
}

/**
 * Gets default email signature
 * @source Original location: UiUtils.ts.backup line 2914
 */
export function getDefaultEmailSignature(templates: Api.TemplatesViewModel) {
	const signatures = sortEmailSignaturesByDefault(templates.signatureTemplates.fetchResults?.toArray());
	const defaultTemplate = signatures?.find(x => x.defaultTemplate);
	return defaultTemplate || signatures?.[0] || undefined;
}
